/**
 * 020 Continuation handoff — the single-use, target-bound warm cross-page
 * continuation (D2). Created when an LLM navigation action moves the user to a
 * new page; held in the singleton ConversationContext (so it survives the
 * navigation/layout transition the prior per-component ref dropped); consumed
 * exactly once on the matching destination, then cleared.
 */

export interface ContinuationHandoff {
  /** Destination surface/route the LLM navigated to. */
  targetSurface: string;
  /** The project the continuation belongs to (when project-scoped). */
  targetProjectShortId?: string;
  /** The conversation to continue at the destination (server-owned id). */
  conversationId: string;
  /**
   * T015 — optional user message enqueued by a navigate_and_send turn action.
   * When set, ConversationContext sends this as the first user message instead
   * of triggering a proactive (Anaka-first) warm_continue start.
   */
  pendingUserMessage?: string;
}

/**
 * Does this pending handoff target the surface the user is now on? Used to
 * decide whether to CONSUME the handoff (warm-continue) or — when it never
 * matches because the user went elsewhere — DISCARD it. A null handoff never
 * matches.
 */
export function handoffMatchesSurface(
  handoff: ContinuationHandoff | null | undefined,
  current: { surface: string; projectShortId?: string },
): boolean {
  if (!handoff) return false;
  if (handoff.targetSurface !== current.surface) return false;
  if (handoff.targetProjectShortId && handoff.targetProjectShortId !== current.projectShortId) {
    return false;
  }
  return true;
}
